import { forwardRef, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Separator, Tooltip } from '~/components';
import { Button } from '~/components/Button';
import { IconButton } from '~/components/IconButton';
import type { SlideOutPanelChildProps } from '~/components/SlideOut/types';
import { Spinner } from '~/components/Spinner';
import { DataUnavailable } from '~/modules/artist-roster/components/DataUnavailable/DataUnavailable';
import { TrackPreReleaseGraphSection } from '~/modules/artist-roster/components/TrackPreReleaseList/TrackPreReleaseGraphSection';
import { useTimeSeriesTrackTargetQuery } from '~/modules/artist-roster/hooks/useTimeSeriesTrackPerformanceQuery';
import type { ArtistDetails } from '~/modules/artist-roster/lib/types';

import { TrackPreReleaseBenchmarks } from './TrackPreReleaseBenchmarks';
import { TrackPreReleaseBenchmarkTag } from './TrackPreReleaseBenchmarkTag';
import { TrackPreReleaseTrackDetails } from './TrackPreReleaseTrackDetails';

interface TrackPreReleaseBenchmarkPanelProps extends SlideOutPanelChildProps {
  artistData: ArtistDetails;
}

export const TrackPreReleaseBenchmarkPanel = forwardRef<
  HTMLDivElement,
  TrackPreReleaseBenchmarkPanelProps
>(({ onClose, artistData }, ref) => {
  const { t } = useTranslation('roster');
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Focus inside the Panel after it opens.
  useEffect(() => {
    closeButtonRef.current?.focus();
  }, [closeButtonRef]);

  // @TODO WMG1-459 - Pass in actual familyId
  const { data, isLoading } = useTimeSeriesTrackTargetQuery({
    familyId: '1234',
  });

  const metrics = useMemo(() => {
    if (!data) {
      return null;
    }

    const streamsResult = [];

    for (let x = 0; x < data.baselineData.length; x++) {
      const value = data.baselineData[x];

      streamsResult.push({
        date: value.unitNumber.toString(),
        x,
        y: value.streamCount,
      });
    }

    return [
      streamsResult.map((value) => ({
        date: value.date,
        x: value.x,
        // @TODO WMG1-489 - Get saved target percentage from read API call
        y: value.y * (data.targetPercentage / 100),
      })),
      streamsResult,
    ];
  }, [data]);

  let content;

  if (isLoading) {
    content = (
      <div className="flex w-full items-center justify-center p-2xl">
        <Spinner />
      </div>
    );
  } else if (metrics) {
    content = (
      <TrackPreReleaseGraphSection
        data={metrics}
        targetPercentage={data?.targetPercentage}
      />
    );
  } else {
    content = (
      <DataUnavailable
        title={t('errorData.title')}
        subtitle={t('errorData.sub')}
        icon={
          <Icon
            type="waveform"
            size="4x"
            variant="light"
            aria-hidden
            className="text-default"
          />
        }
      />
    );
  }

  return (
    <div ref={ref} className="flex basis-full flex-col">
      <div className="flex justify-between p-lg pb-md">
        <h2 className="text-headline-md font-bold">
          {t('trackPreReleaseList.benchmarkPanel.setBenchmark')}
        </h2>
        <IconButton
          ref={closeButtonRef}
          iconType="xmark"
          iconVariant="regular"
          size="large"
          onClick={onClose}
          className="-mr-sm -mt-sm duration-300 ease-out"
          aria-label={t('trackPreReleaseList.benchmarkPanel.closePanel')}
        />
      </div>
      <div className="flex grow flex-col gap-lg overflow-y-scroll px-lg py-md">
        <div className="flex gap-md">
          <TrackPreReleaseTrackDetails {...{ artistData }} />
          <div className="ml-auto flex shrink basis-3/5 flex-col justify-between gap-md rounded-xs px-lg py-md bg-emphasis-default">
            <div>
              <div className="flex items-center gap-sm">
                <div className="flex items-center gap-xs">
                  <div className="font-semibold">
                    {t('trackPreReleaseList.streamingBenchmarks.heading')}
                  </div>
                  {
                    // @TODO WMG1-494 - Replace with a correct tooltip once UX design is finalized
                  }
                  <Tooltip
                    content={t(
                      'trackPreReleaseList.streamingBenchmarks.caption',
                    )}
                    size="sm"
                    side="top"
                    delayDuration={0}
                    sideOffset={8}
                    align="start"
                    contentClassName="max-w-[300px] leading-4"
                  >
                    <Icon type="info-circle" variant="light" size="xs" />
                  </Tooltip>
                </div>
                <TrackPreReleaseBenchmarkTag />
                <div className="ml-auto">
                  <Button variant="text" size="small" className="font-semibold">
                    {t('trackPreReleaseList.benchmarkPanel.giveFeedback')}
                  </Button>
                </div>
              </div>
            </div>
            <TrackPreReleaseBenchmarks />
          </div>
        </div>
        <Separator />
        <div>{content}</div>
      </div>
      <div className="flex justify-end gap-lg border-t-px px-lg py-md bg-level-03 border-emphasis-subtle">
        <Button
          size="large"
          className="disabled:cursor-not-allowed"
          onClick={onClose}
        >
          {t('trackPreReleaseList.benchmarkPanel.setBenchmark')}
        </Button>
      </div>
    </div>
  );
});

TrackPreReleaseBenchmarkPanel.displayName = 'TrackPreReleaseBenchmarkPanel';
